import {
  Box,
  Flex,
  Heading,
  HStack,
  type StackProps,
  Text,
} from "@chakra-ui/layout";
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  IconButton,
  Switch,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type {
  BillingPlanSettingsProjectFragment,
  Maybe,
} from "@src/__generated__/graphql";
import {
  Banner,
  DataTable,
  formatDate,
  LoadingOverlay,
  ModalConfirm,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { can } from "@src/utils/components/permissions";
import { headTitle } from "@src/utils/head-title";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import Head from "next/head";
import { useEffect } from "react";
import { IssueInvoicesModal } from "./components";
import { SendEmailModal } from "./components/send-email-modal";
import { SendEmailToggle } from "./components/send-email-toggle";
import { type BillingPlanTableRow, ProjectBillingPlanStore } from "./store";

type Props = {
  id: string;
};
export const ProjectsInvoicingPlan = observer(function ProjectsBillingPlan({
  id,
}: Props) {
  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new ProjectBillingPlanStore(id, appStore),
  }));

  useEffect(() => {
    store.settings.startPlanLockedChecking();

    return () => {
      clearInterval(store.settings.isLockedInterval);
    };
  }, []);

  return (
    <>
      {store.settings.showLockedWarning && (
        <Banner
          title=""
          status="warning"
          description={store.settings.isLockedWarningDescription}
          mb="4"
          customAction={
            <Button
              colorScheme="orange"
              leftIcon={<Icon name="refresh-cw-02" />}
              onClick={store.refetch}
            >
              <Trans>Refresh</Trans>
            </Button>
          }
        />
      )}
      <Head>
        <title>{headTitle(store.settings.project?.title)}</title>
      </Head>

      <DataTable>
        <Box px="4" borderBottom="1px" borderBottomColor="gray.200">
          <NavigationProject id={id} />
        </Box>

        <LoadingOverlay
          isLoading={
            store.settings.isFetching ||
            store.settings.isLocking ||
            store.plan.isFetching
          }
          position="absolute"
        >
          <DataTable.NavBar>
            <Box py="4" minH="16">
              <HStack justify="space-between">
                {!store.settings.isFetching ? (
                  <HStack spacing="4" alignItems="center">
                    <Heading as="h2" fontSize="xl">
                      <Trans>Invoicing:</Trans>
                    </Heading>
                    &nbsp;
                    <ButtonGroup
                      isAttached
                      isDisabled={!store.settingsEditEnabled}
                    >
                      <Button
                        onClick={() => {
                          store.handleShowBudgetItems(false);
                        }}
                        variant={
                          !store.settings.showBudgetItems.value
                            ? "solid"
                            : "outline"
                        }
                      >
                        <Trans>Manual</Trans>
                      </Button>
                      <Button
                        onClick={() => {
                          store.handleShowBudgetItems(true);
                        }}
                        variant={
                          store.settings.showBudgetItems.value
                            ? "solid"
                            : "outline"
                        }
                      >
                        <Trans>Automated</Trans>
                      </Button>
                    </ButtonGroup>
                    {store.settings.showBudgetItems.value && (
                      <>
                        <FormControl alignItems="center" display="flex">
                          <Switch
                            id="issue-invoices-automatically"
                            isChecked={
                              store.settings.issueInvoicesForm?.$.isChecked
                                .value
                            }
                            isDisabled={!store.settingsEditEnabled}
                            onChange={(event) => {
                              store.settings.issueInvoicesForm?.handleSwitchToggle(
                                event,
                              );
                              if (event.target.checked) return;
                              store.clearAccountingColumns();
                            }}
                          />
                          <FormLabel
                            mb="0"
                            color={
                              store.settings.issueInvoicesForm?.formHasError
                                ? "red"
                                : undefined
                            }
                            htmlFor="issue-invoices-automatically"
                            marginInlineStart="1"
                          >
                            <Trans>Issue invoices automatically</Trans>
                            &nbsp;
                            {store.settings.issueInvoicesForm?.$.isChecked
                              .value && (
                              <Flex display="inline-flex">
                                <Button
                                  onClick={
                                    store.settings.issueInvoicesForm
                                      ?.popoverState.open
                                  }
                                  size="sm"
                                  variant="link"
                                >
                                  {store.settings.isPlanLocked ? (
                                    <Trans>(show)</Trans>
                                  ) : (
                                    <Trans>(edit)</Trans>
                                  )}
                                </Button>
                              </Flex>
                            )}
                          </FormLabel>
                        </FormControl>
                        <SendEmailToggle
                          form={store.settings.sendInvoicesForm}
                          label={<Trans>Send invoices automatically</Trans>}
                          isEditable={store.settingsEditEnabled}
                          isPlanLocked={store.settings.isPlanLocked}
                          switchId="send-invoices-automatically"
                          onEnable={store.settings.updateEmailTemplates}
                        />
                        <SendEmailToggle
                          form={store.settings.sendThankYouForm}
                          label={
                            <Trans>Auto-send payment confirmation email</Trans>
                          }
                          isEditable={store.settingsEditEnabled}
                          isPlanLocked={store.settings.isPlanLocked}
                          switchId="send-thank-you-automatically"
                          onEnable={store.settings.updateEmailTemplates}
                        />
                      </>
                    )}
                  </HStack>
                ) : (
                  <Box />
                )}
                <ConfirmButtons
                  isSaveDisabled={store.isSaveDisabled}
                  onCancel={store.handleCancel}
                  onConfirm={store.handleSave}
                  padding="0"
                  lockedAt={
                    store.settings.project?.billingPlanSetting?.locked_at
                  }
                  onLock={store.handleLockPlan}
                  isLocking={store.settings.isLocking}
                  lockedByUser={
                    store.settings.project?.billingPlanSetting?.lockedByUser
                  }
                />
              </HStack>
            </Box>
          </DataTable.NavBar>
          <DataTable.Body<BillingPlanTableRow>
            data={store.tableData}
            tableKey={store.tableKey}
            columns={store.columns}
            hideExpandColumn
            enableColumnOrdering={false}
            beforeVisibilityControl={
              !store.settings.isPlanLocked ? (
                <Tooltip label={t`Add month`} placement="top">
                  <IconButton
                    aria-label={t`Add next month icon button`}
                    colorScheme="gray"
                    icon={<Icon name="plus" />}
                    onClick={store.handleAddMonth}
                    variant="ghost"
                  />
                </Tooltip>
              ) : undefined
            }
          />
          <ConfirmButtons
            isSaveDisabled={store.isSaveDisabled}
            onCancel={store.handleCancel}
            onConfirm={store.handleSave}
            lockedAt={store.settings.project?.billingPlanSetting?.locked_at}
            onLock={store.handleLockPlan}
            isLocking={store.settings.isLocking}
            lockedByUser={
              store.settings.project?.billingPlanSetting?.lockedByUser
            }
          />
        </LoadingOverlay>
      </DataTable>

      <ModalConfirm
        isOpen={store.confirmModalState.isOpen}
        onClose={store.confirmModalState.onClose}
        onConfirm={store.handleModalConfirm}
        heading={t`Unsaved changes`}
        message={
          <Trans>
            You will lose unsaved changes, do you still wish to proceed?
          </Trans>
        }
      />

      <SendEmailModal
        form={store.settings.sendInvoicesForm}
        title={<Trans>Send mail</Trans>}
        isEditable={store.settingsEditEnabled}
      />

      <SendEmailModal
        form={store.settings.sendThankYouForm}
        title={<Trans>Send payment confirmation email</Trans>}
        isEditable={store.settingsEditEnabled}
      />

      <IssueInvoicesModal
        isOpen={store.settings.issueInvoicesForm?.popoverState.isOpen ?? false}
        onClose={store.settings.issueInvoicesForm?.popoverState.close}
        form={store.settings.issueInvoicesForm}
        vatRateOptions={store.settings.vatRateOptions}
        isDisabled={store.issueInvoicesInputsDisabled}
        onInvoiceItemsChange={store.handleInvoiceItemsChange}
        onBillingEntityChange={store.handleBillingEntityChange}
        onContactChange={store.handleContactChange}
      />

      <ModalConfirm
        isOpen={store.billingEntityChangeConfirmState.isOpen}
        onClose={store.handleBillingEntityChangeModalClose}
        onConfirm={store.handleBillingEntityChangeConfirm}
        heading={t`Update email recipients`}
        message={
          store.billingEntityChangeConfirmState.additionalData?.type ===
          "contact-person" ? (
            <Trans>
              Contact person has been changed. Email recipients will be updated.
              Are you sure you want to proceed?
            </Trans>
          ) : (
            <Trans>
              Billing address has been changed. Email recipients will be
              regenerated. Are you sure you want to proceed?
            </Trans>
          )
        }
      />
    </>
  );
});

type ConfirmButtonsProps = {
  onConfirm: () => void;
  onCancel: () => void;
  isSaveDisabled: boolean;
  padding?: StackProps["padding"];
  lockedAt: Maybe<string>;
  isLocking: boolean;
  onLock: () => void;
  lockedByUser: Maybe<
    NonNullable<
      BillingPlanSettingsProjectFragment["billingPlanSetting"]
    >["lockedByUser"]
  >;
};
const ConfirmButtons = observer(function ConfirmButtons({
  onConfirm,
  onCancel,
  isSaveDisabled,
  padding = "4",
  lockedAt,
  isLocking,
  onLock,
  lockedByUser,
}: ConfirmButtonsProps) {
  const isLocked = Boolean(lockedAt);
  const canLock =
    can("project_lockBillingPlan") && (!isSaveDisabled || isLocked);

  return (
    <HStack justify="end" spacing="2" p={padding}>
      {!isLocked && (
        <>
          <Button colorScheme="gray" onClick={onCancel} variant="subtle">
            <Trans>Reset</Trans>
          </Button>
          <Button isDisabled={isSaveDisabled} onClick={onConfirm}>
            <Trans>Update</Trans>
          </Button>
        </>
      )}

      <Tooltip
        isDisabled={!canLock && !isLocked}
        label={
          isLocked ? (
            <Text>
              <Trans>Locked by</Trans>&nbsp;
              {lockedByUser?.full_name}
              &nbsp;
              <Trans>on</Trans>&nbsp;
              {lockedAt ? formatDate(new Date(lockedAt)) : null}
              {canLock && (
                <>
                  <br />
                  <Trans>Click to unlock</Trans>
                </>
              )}
            </Text>
          ) : (
            <>
              <Trans>Lock invoicing plan</Trans>
            </>
          )
        }
        placement="top"
      >
        <IconButton
          aria-label={t`Invoicing plan lock trigger`}
          icon={
            isLocked ? (
              <Icon name="lock-01" />
            ) : (
              <Icon name="lock-unlocked-01" />
            )
          }
          isDisabled={!canLock}
          isLoading={isLocking}
          onClick={onLock}
          variant="ghost"
        />
      </Tooltip>
    </HStack>
  );
});
